import { useMemo } from "react";
import { useCalculateWinningTax } from "./useCalculateWinningTax";
import { useCalculateOdds } from "./useCalculateOdds";
import { useCalculateVat } from "./useCalculateVat";
import { useCalculateMaxPayout } from "./useCalculateMaxPayout";
import { BetSlipMode } from "../types/enums";
import { buildModalChanges } from "../shared/utils/helpers";

// VAT is deducted from the stake before the winning-tax tier lookup runs -
// mirrors legacy's dealWithExciseTax(), which returns the net-of-VAT stake
// that accumulator()/multiplicator() then feed into the tax calculation.
const deriveNetStakes = (
  stakes: { [key: string]: number },
  mode: BetSlipMode,
  exciseTaxRate: number,
): { [key: string]: number } => {
  if (exciseTaxRate <= 0) return stakes;
  const divisor = 1 + exciseTaxRate / 100;
  if (mode === BetSlipMode.Single) {
    const netStakes: { [key: string]: number } = {};
    Object.entries(stakes).forEach(([key, gross]) => {
      netStakes[key] = gross / divisor;
    });
    return netStakes;
  }
  return { [BetSlipMode.Combo]: (stakes[BetSlipMode.Combo] ?? 0) / divisor };
};

export function useConfirmBetChangesData({
  message,
  betSlipState,
  individualStakes,
  globalStake,
  taxConfig,
  selectedBonus,
  winBoostAmount,
  preChangesIndividualStakes,
  preChangesGlobalStake,
  preChangesOdds,
}: {
  message: AuthMessageLike | null;
  betSlipState: IBetSlipState;
  individualStakes: { [key: string]: number };
  globalStake: number;
  taxConfig: ITaxCalculationConfig;
  selectedBonus?: any;
  winBoostAmount: number;
  preChangesIndividualStakes?: { [key: string]: number };
  preChangesGlobalStake?: number;
  preChangesOdds?: number | null;
}): ConfirmBetChangesData | null {
  const stakes = useMemo(
    () =>
      betSlipState.mode === BetSlipMode.Single
        ? individualStakes
        : { combo: globalStake },
    [betSlipState.mode, individualStakes, globalStake],
  );

  // Original values — use pre-changes snapshot when available so the modal
  // shows the stake the user entered, not the new authorised amount.
  const originalStake = useMemo(() => {
    if (betSlipState.mode === BetSlipMode.Single) {
      const source = preChangesIndividualStakes ?? individualStakes;
      return Object.values(source).reduce((sum: number, s: number) => sum + s, 0);
    }
    return preChangesGlobalStake ?? globalStake;
  }, [betSlipState.mode, individualStakes, globalStake, preChangesIndividualStakes, preChangesGlobalStake]);

  const { exciseTaxRate } = useCalculateVat({
    selections: betSlipState.selections,
    config: taxConfig,
    stakes,
    mode: betSlipState.mode,
  });

  const netStakes = useMemo(
    () => deriveNetStakes(stakes, betSlipState.mode, exciseTaxRate),
    [stakes, betSlipState.mode, exciseTaxRate],
  );

  const { totalTaxValue: winningTax, taxPercentage } = useCalculateWinningTax({
    selections: betSlipState.selections,
    config: taxConfig,
    bonus: selectedBonus,
    stakes: netStakes,
    mode: betSlipState.mode,
    bonusValue: winBoostAmount,
    betBuilderOdds: betSlipState.betBuilderState?.totalOdds || [],
  });

  // Live selections already carry the new price by the time this hook runs
  // (Summary.tsx applies UPDATE_SELECTIONS_FROM_RECOVERY as soon as the modal
  // opens), so the combo total recalculated here is the *new* odds, not the
  // original. preChangesOdds is the snapshot taken before that dispatch.
  const liveOdds = useCalculateOdds({
    selections: betSlipState.selections,
    betBuilderOdds: betSlipState.betBuilderState?.totalOdds || [],
    mode: betSlipState.mode,
  });
  const originalOdds = preChangesOdds ?? liveOdds;

  const maxPayout = useCalculateMaxPayout({
    selections: betSlipState.selections,
    stakes: netStakes,
    mode: betSlipState.mode,
    totalOdds: originalOdds,
    totalTaxValue: winningTax,
    winBoostAmount,
    isFreeBetActive: false,
  });

  // New values (from message)
  const newStake = useMemo(() => {
    if (!message?.rowChanges?.length) return originalStake;
    if (betSlipState.mode === BetSlipMode.Combo) {
      return message.rowChanges[0].newAmount;
    }
    // Single: sum of all row changes
    return message.rowChanges.reduce((sum, rc) => sum + rc.newAmount, 0);
  }, [message, betSlipState.mode, originalStake]);

  const updatedSelections = useMemo((): IBetSlipSelection[] => {
    if (!message?.betChanges?.length) return betSlipState.selections;

    return betSlipState.selections.map((sel) => {
      const bc = message.betChanges!.find(
        (c) =>
          c.selections.includes(sel.eventId) &&
          c.betTypeId === sel.betTypeId &&
          (sel.oddName === c.oddName || sel.oddKey === c.oddName),
      );
      return bc ? { ...sel, oddPrice: bc.newPrice } : sel;
    });
  }, [message, betSlipState.selections]);

  const updatedBetBuilderOdds = useMemo((): IBetBuilderTotalOdds[] => {
    if (
      !message?.betChanges?.length ||
      !betSlipState.betBuilderState?.totalOdds?.length
    ) {
      return betSlipState.betBuilderState?.totalOdds || [];
    }

    // Group betChanges by orderRowId (each row = one bet builder event)
    const oddsByEvent = new Map<number, number[]>();
    message.betChanges.forEach((bc) => {
      const eventId = bc.selections[0];
      if (!oddsByEvent.has(eventId)) {
        oddsByEvent.set(eventId, []);
      }
      oddsByEvent.get(eventId)!.push(bc.newPrice);
    });

    return betSlipState.betBuilderState.totalOdds.map((to) => {
      const newPrices = oddsByEvent.get(to.eventId);
      if (newPrices && newPrices.length > 0) {
        const newOdd = newPrices.reduce((acc, p) => acc * p, 1);
        return { ...to, odd: Number(newOdd.toFixed(2)) };
      }
      return to;
    });
  }, [message, betSlipState.betBuilderState?.totalOdds]);

  const newStakes = useMemo(() => {
    if (!message?.rowChanges?.length) return stakes;

    if (betSlipState.mode === BetSlipMode.Combo) {
      return { combo: message.rowChanges[0].newAmount };
    }

    // Single: map rowChanges to selection keys
    const result = { ...individualStakes };
    message.rowChanges.forEach((rc) => {
      const selection = betSlipState.selections.find((s) =>
        rc.selections.includes(s.eventId),
      );
      if (selection) {
        result[selection.key] = rc.newAmount;
      }
    });
    return result;
  }, [
    message,
    betSlipState.mode,
    betSlipState.selections,
    individualStakes,
    stakes,
  ]);

  const { exciseTaxValue: newExciseTaxValue, exciseTaxRate: newExciseTaxRate } =
    useCalculateVat({
      selections: updatedSelections,
      config: taxConfig,
      stakes: newStakes,
      mode: betSlipState.mode,
    });

  const newNetStakes = useMemo(
    () => deriveNetStakes(newStakes, betSlipState.mode, newExciseTaxRate),
    [newStakes, betSlipState.mode, newExciseTaxRate],
  );

  const newOdds = useCalculateOdds({
    selections: updatedSelections,
    betBuilderOdds: updatedBetBuilderOdds,
    mode: betSlipState.mode,
  });

  const { totalTaxValue: newWinningTax } = useCalculateWinningTax({
    selections: updatedSelections,
    config: taxConfig,
    bonus: selectedBonus,
    stakes: newNetStakes,
    mode: betSlipState.mode,
    bonusValue: winBoostAmount,
    betBuilderOdds: updatedBetBuilderOdds,
  });

  const newMaxPayout = useCalculateMaxPayout({
    selections: updatedSelections,
    stakes: newNetStakes,
    mode: betSlipState.mode,
    totalOdds: newOdds,
    totalTaxValue: newWinningTax,
    winBoostAmount,
    isFreeBetActive: false,
  });

  const changes = useMemo(() => {
    if (!message) return [];
    return buildModalChanges(
      message,
      betSlipState,
      preChangesIndividualStakes ?? individualStakes,
      preChangesGlobalStake ?? globalStake,
      originalOdds,
      newOdds,
    );
  }, [
    message,
    betSlipState,
    individualStakes,
    globalStake,
    preChangesIndividualStakes,
    preChangesGlobalStake,
    originalOdds,
    newOdds,
  ]);

  const eventName = useMemo(() => {
    if (changes.length > 0) return changes[0].eventName;
    const firstSel = betSlipState.selections[0];
    const firstBet = betSlipState.betBuilderState?.bets?.[0];
    if (firstSel) {
      return `${firstSel.homeName || ""} - ${firstSel.awayName || ""}`.trim();
    }
    return firstBet?.eventName || "Event";
  }, [changes, betSlipState.selections, betSlipState.betBuilderState?.bets]);

  if (!message) return null;

  const oddsChanged = !!message.betChanges?.some((bc) => bc.isChanged);
  const stakeChanged = !!message.rowChanges?.some((rc) => rc.isChanged);

  return {
    changes,
    originalStake,
    newStake,
    originalOdds,
    newOdds,
    eventName,
    winningTax,
    newWinningTax,
    maxPayout,
    newMaxPayout,
    taxPercentage,
    oddsChanged,
    stakeChanged,
    newExciseTaxValue,
    newExciseTaxRate,
  };
}
